import * as R from 'ramda'
import * as React from 'react'
import * as ReactRedux from 'react-redux'
import * as ReactRouterDom from 'react-router-dom'
import * as Urql from 'urql'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'

import * as Common from '@rushplay/common'
import * as Triggers from '@rushplay/triggers'
import { Global, css } from '@emotion/core'

import * as Session from './session'
import * as CombinedSelectors from './combined-selectors'
import * as Player from './player'
import * as ServerConfiguration from './server-configuration'
import * as Configuration from './configuration'
import { useMenuQueries } from './use-menu-queries'

const getBetbyToken = `
  query getBetbyToken {
    __typename
    player {
      __typename
      betbyToken
    }
  }
`

function usePreviousValue(value) {
  const ref = React.useRef(value)
  React.useEffect(() => {
    ref.current = value
  })
  return ref.current
}

function setOffsetstickyTop(token) {
  if (window.innerWidth > 1000) {
    return 0
  } else {
    if (!token || window.innerWidth < 800) {
      return 51
    }
    return 56
  }
}

function setOffsetTop(token) {
  if (window.innerWidth > 1000) {
    return 45
  } else {
    if (!token || window.innerWidth < 800) {
      return 60
    }
    return 66
  }
}

const hasSeenSports = CombinedSelectors.createSeenSportsSelector()
const getSportsTrigger = Triggers.createGetTriggerSelector()

export function SportsbookDirect(props) {
  const { depositQuery, loginQuery, registerQuery } = useMenuQueries()
  const ref = React.useRef(null)
  const [response] = Urql.useQuery({
    query: getBetbyToken,
  })
  const [shouldInitApp, setShouldInitApp] = React.useState(false)
  const [btRendererInited, setBtRendererInited] = React.useState(false)
  const [scrollbarWidth, setScrollbarWidth] = React.useState('0px')
  const history = ReactRouterDom.useHistory()
  const dispatch = ReactRedux.useDispatch()
  const sportsSeen = ReactRedux.useSelector(hasSeenSports)
  const sportsTrigger = ReactRedux.useSelector(state =>
    getSportsTrigger(state.triggers, {
      kind: 'sports',
      event: 'onboarding',
    })
  )
  const sessionToken = ReactRedux.useSelector(state =>
    Session.getToken(state.session)
  )
  const apiUrl = ReactRedux.useSelector(state =>
    Configuration.getApiUrl(state.configuration)
  )
  // TODO: Use safe navigation once razzle is updated
  const token = response?.data?.player?.betbyToken
  const countryCode = ReactRedux.useSelector(state =>
    Configuration.getCountryCode(state.configuration)
  )
  const brandName = ReactRedux.useSelector(state =>
    Configuration.getBrand(state.configuration)
  )
  const { cdnPrefix, locale } = ServerConfiguration.useContext()
  const prevToken = usePreviousValue(token)
  const prevLanguage = usePreviousValue(locale.language)
  const betbySettings = {
    boom: {
      staging: {
        brandId: '2054213474539675648',
        scriptUrl: 'https://ui.invisiblesport.com/bt-renderer.min.js',
        themeName: 'boom-casino',
      },
      production: {
        brandId: '2065448311103565824',
        scriptUrl: 'https://boomcasino.sptpub.com/bt-renderer.min.js',
        themeName: 'boom-casino',
      },
    },
    playboom: {
      staging: {
        brandId: '2116526784068591616',
        scriptUrl: 'https://ui.invisiblesport.com/bt-renderer.min.js',
        themeName: 'boom-casino',
      },
      production: {
        brandId: '2120214968988209152',
        scriptUrl: 'https://playboom.sptpub.com/bt-renderer.min.js',
        themeName: 'boom-casino',
      },
    },
    playboom24: {
      staging: {
        brandId: '2116526784068591616',
        scriptUrl: 'https://ui.invisiblesport.com/bt-renderer.min.js',
        themeName: 'boom-casino',
      },
      production: {
        brandId: '2120214968988209152',
        scriptUrl: 'https://playboom.sptpub.com/bt-renderer.min.js',
        themeName: 'boom-casino',
      },
    },
    wettenlive: {
      staging: {
        brandId: '2191604935899090944',
        scriptUrl: 'https://ui.invisiblesport.com/bt-renderer.min.js',
        themeName: 'wettenlive',
      },
      production: {
        brandId: '2207245396944760832',
        scriptUrl: 'https://wettenlive.sptpub.com/bt-renderer.min.js',
        themeName: 'wettenlive',
      },
    },
    casinosky: {
      staging: {
        brandId: '2290906544948252672',
        scriptUrl: 'https://ui.invisiblesport.com/bt-renderer.min.js',
        themeName: 'casinosky',
      },
      production: {
        brandId: '2297826855979458560',
        scriptUrl: 'https://casinosky.sptpub.com/bt-renderer.min.js',
        themeName: 'casinosky',
      },
    },
    casinoskyjp: {
      staging: {
        brandId: '2290906544948252672',
        scriptUrl: 'https://ui.invisiblesport.com/bt-renderer.min.js',
        themeName: 'casinosky',
      },
      production: {
        brandId: '2297826855979458560',
        scriptUrl: 'https://casinosky.sptpub.com/bt-renderer.min.js',
        themeName: 'casinosky',
      },
    },
    paradiseplay: {
      staging: {
        brandId: '2290906544948252672',
        scriptUrl: 'https://ui.invisiblesport.com/bt-renderer.min.js',
        themeName: 'casinosky',
      },
      production: {
        brandId: '2297826855979458560',
        scriptUrl: 'https://casinosky.sptpub.com/bt-renderer.min.js',
        themeName: 'casinosky',
      },
    },
  }

  const brandId = R.path([brandName, cdnPrefix, 'brandId'], betbySettings)
  const scriptUrl = R.path([brandName, cdnPrefix, 'scriptUrl'], betbySettings)
  const themeName = R.path([brandName, cdnPrefix, 'themeName'], betbySettings)
  const sportsUrl = '/sports'

  // Makes sure client is available on window (script loaded properly)
  React.useEffect(() => {
    if (!btRendererInited) {
      const timerId = window.setInterval(() => {
        setBtRendererInited(typeof window.BTRenderer !== 'undefined')
      }, 250)
      return () => {
        clearInterval(timerId)
      }
    }
  }, [btRendererInited])

  const client = React.useMemo(() => {
    if (process.browser && btRendererInited) {
      return new window.BTRenderer()
    }
  }, [process.browser, btRendererInited])

  const widgetConfig = React.useMemo(
    () =>
      props.widgetName
        ? {
            widgetName: props.widgetName,
            widgetParams: {
              minFrameHeight: props.minFrameHeight,
              placeholder: props.widgetPlaceholder,
              onBannerClick: ({ url }) =>
                history.push(`${sportsUrl}?bt-path=${url}`),
            },
          }
        : {},
    [props.widgetName, props.minFrameHeight, props.widgetPlaceholder]
  )

  const lang =
    countryCode == 'CA'
      ? `${locale.language}-${countryCode.toLowerCase()}`
      : locale.language

  // Initiates client
  React.useEffect(() => {
    if (shouldInitApp) {
      setShouldInitApp(false)
      const offsetTop = setOffsetTop(token)
      const offsetStickyTop = setOffsetstickyTop(token)

      client.initialize({
        brand_id: brandId,
        token,
        themeName: themeName,
        lang: lang,
        target: document.getElementById('betby'),
        betSlipOffsetBottom: 0,
        betSlipOffsetTop: offsetTop,
        stickyTop: offsetStickyTop,
        betslipZIndex: 100,
        cssUrls: [
          'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
        ],
        fontFamilies: ['Montserrat, sans-serif', 'Roboto, sans-serif'],
        ...widgetConfig,
        onSessionRefresh: () => window.location.reload(),
        goToClick: ({ url }) => history.push(`${sportsUrl}?bt-path=${url}`),
        onBannerClick: ({ url }) => history.push(`${sportsUrl}?bt-path=${url}`),
        onLogin: () => history.push(`?${loginQuery}`),
        onRecharge: () => history.push(`?${depositQuery}`),
        onRegister: () => history.push(`?${registerQuery}`),
        onSportClick: () => history.push(sportsUrl),
        onTokenExpired: () =>
          fetch(`${apiUrl}/my/betby_jwt_token`, {
            headers: {
              authorization: sessionToken ?? null,
              'content-type': 'application/json',
              accept: 'application/vnd.casinosaga.v1',
            },
            method: 'GET',
          })
            .then(res => res.json())
            .then(res => res.token)
            .catch(err => err),
      })
    }
  }, [locale, token, shouldInitApp, client, response.fetching])

  // Hook for checking if app should be reinited.
  React.useEffect(() => {
    // Do nothing if there is no refrence
    if ((ref.current, btRendererInited)) {
      // Config has been updated; e.g. language was switched.
      if (
        prevLanguage !== locale.language ||
        (prevToken == null && token != null)
      ) {
        client.kill()
        setShouldInitApp(true)
      }
    }
  }, [
    token,
    locale,
    prevToken,
    prevLanguage,
    ref,
    setShouldInitApp,
    btRendererInited,
    response.fetching,
  ])

  React.useEffect(() => {
    const queries = {
      sm: '(max-width: 799px)',
      md: '(min-width: 800px) and (max-width: 999px)',
      lg: '(min-width: 1000px)',
    }
    const mediaQueryLists = {}
    const keys = R.keys(queries)

    function handleChange(isMatching) {
      const match = R.filter(query => query, isMatching)

      switch (R.head(R.keys(match))) {
        case 'sm': {
          client.updateOptions({ stickyTop: 46, betSlipOffsetTop: 46 })
          break
        }
        case 'md': {
          if (token) {
            client.updateOptions({ stickyTop: 56, betSlipOffsetTop: 56 })
          } else {
            client.updateOptions({ stickyTop: 46, betSlipOffsetTop: 46 })
          }
          break
        }
        case 'lg': {
          client.updateOptions({ stickyTop: 0, betSlipOffsetTop: 0 })
          break
        }
      }
    }
    const handleQueryListener = () => {
      const isMatching = R.reduce(
        (acc, media) => {
          acc[media] = Boolean(
            mediaQueryLists[media] && mediaQueryLists[media].matches
          )
          return acc
        },
        {},
        R.keys(queries)
      )

      handleChange(isMatching)
    }

    R.forEach(media => {
      mediaQueryLists[media] = window.matchMedia(queries[media])
    }, keys)

    R.forEach(media => {
      mediaQueryLists[media].addListener(handleQueryListener)
    }, keys)

    return () => {
      R.forEach(media => {
        mediaQueryLists[media].removeListener(handleQueryListener)
      }, keys)
    }
  }, [client])

  React.useEffect(() => {
    if (process.browser) {
      setScrollbarWidth(window.innerWidth - document.body.clientWidth + 'px')
    }
  }, [process.browser])

  // Launch client
  React.useEffect(() => {
    if (ref.current && brandId && btRendererInited) {
      setShouldInitApp(true)
    }
  }, [ref.current, brandId, btRendererInited])

  React.useEffect(() => {
    if (!sportsSeen) {
      dispatch([
        !token && Player.updateSportsSeen(true),
        token && sportsTrigger && Triggers.removeSelected(sportsTrigger.id),
      ])
    }
  }, [token, sportsSeen, sportsTrigger, dispatch])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (client) {
        client.kill()
      }
    }
  }, [client])

  return (
    <React.Fragment>
      <Global
        styles={css`
          body {
            overflow-x: visible;
          }
        `}
      />
      <Helmet>
        <script src={scriptUrl} id="betby-script" />
      </Helmet>
      <Common.Box
        id="betby"
        ref={ref}
        width={
          props.widgetName
            ? '100%'
            : ['100vw', null, `calc(100vw - 200px - ${scrollbarWidth})`]
        }
      />
    </React.Fragment>
  )
}

SportsbookDirect.propTypes = {
  widgetName: PropTypes.string,
  widgetPlaceholder: PropTypes.string,
  minFrameHeight: PropTypes.number,
}
